import {
  ApplicationRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CurrencyList } from 'src/app/models/currency.model';
import { LanguageList } from 'src/app/models/language.model';
import { CountryList } from 'src/app/models/country.model';
import { KycConsentConfig } from 'src/app/models/kycConsentConfig.model';
import { Translation } from 'src/app/models/translation.model';
import { BPSAdminLevel } from 'src/app/models/user.model';
import {
  BankDetails,
  CodedAssetType,
  CodePattern,
  CodePatternOptions,
  DashboardConfiguration,
  MapConfiguration,
  UtilityDetails,
} from 'src/app/models/utility.model';
import { ValidationRule } from 'src/app/models/validationRule.model';
import { UtilityService } from 'src/app/services/utility.service';
import { Config } from 'ngx-ui-loader/lib/utils/interfaces';
import { ConfigurationGeneration, ConfigurationGenerationPropertyType, FormatType } from 'src/app/models/configurationGeneration.model';
import { SubstationTemplateService } from 'shared-lib';
import { BlobStorageService } from 'src/app/_modules/shared/_services/blob-storage.service';
import { ContainerNames } from 'src/app/models/containerNames.model';

interface ScopedValidationRule extends ValidationRule {
  scopeType?: string | number | null;
  scopeId?: number | null;
  scopeLabel?: string | null;
}

interface SpecialValidationTypeConfig {
  scopeType: string;
  fieldLabel: string;
  placeholder: string;
  required: boolean;
  loaderKey: string;
  optionIdKey: string;
  optionLabelKey: string;
}

interface ScopeOption {
  id: number;
  label: string;
}

@Component({
  selector: 'app-utility-details',
  templateUrl: './utility-details.component.html',
  styleUrls: ['./utility-details.component.scss'],
})
export class UtilityDetailsComponent implements OnInit {
  @ViewChild('closeModal') closeModal: ElementRef;
  @ViewChild('closeValidationModal') closeValidationModal: ElementRef;
  @ViewChild('closeConfigurationGenerationModal') closeConfigurationGenerationModal: ElementRef;
  @ViewChild('closeMapModalBtn') closeMapModalBtn: ElementRef;

  @ViewChild('utilityImage') utilityImageInput: ElementRef;
  @ViewChild('closeCodeModal') closeCodeModal: ElementRef;
  @ViewChild('serviceImage') serviceImageInput: ElementRef;
  id: number;
  editEnabled = false;
  public Configuration = DashboardConfiguration;
  selectedUtility: UtilityDetails;
  cinAssetTypes: string[] = [
    'Disco',
    'Sub-Disco',
    'Underground',
    'Type',
    '33kV/11kV Trans Substation',
    'Power Transformer',
    '11kV Feeder',
    '11kV Distribution Poles',
    '11kV/400V Transformer',
    'Upriser',
    'Service (LT) Pole',
    'Service Wire',
    'Customer',
  ];
  utilityImage: File | null;
  serviceImage: File | null;
  imageSrc: string | ArrayBuffer;
  selectedTranslation: Translation = new Translation();
  selectedCodePattern: CodePattern = new CodePattern();
  selectedValidationRule: ScopedValidationRule = new ValidationRule();
  selectedConfigurationGeneration: ConfigurationGeneration = new ConfigurationGeneration();
  startsWith: string;
  letterNo: number;
  isNumber: boolean;
  defaultCountry: string = 'Ghana';
  currencyList = CurrencyList;
  countryList = CountryList;
  languageList = LanguageList;
  selectedCodePatternOption?: CodePatternOptions;
  isMapVisible: boolean = false;
  isAddNewConfigurationGenerationEnabled: boolean = true;
  limitValue: number | null = null;

  validationRuleTypeOptions: { value: number; label: string }[] = [
    { value: 1, label: 'AccountNumber' },
    { value: 3, label: 'BuildingSlrn' },
    { value: 4, label: 'MeterNumber' },
    { value: 2, label: 'MeterSLRN' },
    { value: 6, label: 'PoleSlrn' },
    { value: 5, label: 'TransformerSlrn' },
    { value: 7, label: 'IdCardNumber' },
    { value: 8, label: 'RmuSlrn' },
    { value: 9, label: 'MvPoleSlrn' },
    { value: 10, label: 'DtNumber' },
    { value: 11, label: 'SubstationTemplate' },
  ];
  specialTypeMap: Record<number, SpecialValidationTypeConfig> = {
    11: {
      scopeType: 'SubstationTemplate',
      fieldLabel: 'Substations',
      placeholder: 'Select substation',
      required: true,
      loaderKey: 'substationTemplate',
      optionIdKey: 'id',
      optionLabelKey: 'className',
    },
  };
  optionsByLoaderKey: Record<string, ScopeOption[]> = {};
  private originalValidationRulePropertyType: number | null = null;
  private originalValidationRuleScopeId: number | null = null;
  private originalValidationRuleIndex: number | null = null;

  configurationGenerationTypeOptions: { value: number; label: string }[] = [
    { value: 1, label: 'AccountNumber' },
    { value: 2, label: 'Spn' },
  ];

  availableValidationRuleTypeOptions: { value: number; label: string }[] = [];
  availableConfigurationGenerationTypeOptions: { value: number; label: string }[] = [];
  isEditingValidationRule = false;
  isEditConfigurationGeneration: boolean = false;
  bpsAdminLevel: BPSAdminLevel;

  getFormatTypeLabel(formatType: FormatType | null): string {
    switch (formatType) {
      case FormatType.Numeric:
        return "Numeric";
      case FormatType.Alphanumeric:
        return "Alphanumeric";
      case FormatType.AlphanumericWithSigns:
        return "Alphanumeric With Signs";
      default:
        return "Unknown";
    }
  }

  getConfigurationGenerationPropertyTypeLabel(formatType: ConfigurationGenerationPropertyType | null): string {
    switch (formatType) {
      case ConfigurationGenerationPropertyType.AccountNumber:
        return "AccountNumber";
      case ConfigurationGenerationPropertyType.Spn:
        return "Spn";
      default:
        return "Unknown";
    }
  }

  codePatternOptions = [
    {
      key: CodePatternOptions.TripleNumeric,
      value: 'Numeric 100 (Default)',
    },
    {
      key: CodePatternOptions.SingleNumeric,
      value: 'Single Numeric',
    },
    {
      key: CodePatternOptions.DoubleNumeric,
      value: 'Double Numeric',
    },
    {
      key: CodePatternOptions.QuadrupleNumeric,
      value: 'Numeric 1000',
    },
    {
      key: CodePatternOptions.SingleAlphabet,
      value: 'Single Alphabet',
    },
    {
      key: CodePatternOptions.DoubleAlphabet,
      value: 'Double Alphabet',
    },
    {
      key: CodePatternOptions.TripleAlphabet,
      value: 'Triple Alphabet',
    },
    {
      key: CodePatternOptions.AlphaNumericSingle,
      value: 'Alphanumeric Single',
    },
    {
      key: CodePatternOptions.AlphaNumericDouble,
      value: 'Alphanumeric 10',
    },
    {
      key: CodePatternOptions.AlphaNumericTriple,
      value: 'Alphanumeric 100',
    },
    {
      key: CodePatternOptions.AlphaNumericQuadruple,
      value: 'Alphanumeric 1000',
    },
  ];

  assetTypeOptions = [
    {
      key: CodedAssetType.TransmissionStation,
      name: 'Transmission Station',
    },
    {
      key: CodedAssetType.Feeder33,
      name: '33KV Feeder',
    },
    {
      key: CodedAssetType.Substation,
      name: 'Injection Substation',
    },
    {
      key: CodedAssetType.Feeder11,
      name: '11KV Feeder',
    },
    {
      key: CodedAssetType.Dt,
      name: 'Distribution Substation',
    },
    {
      key: CodedAssetType.Pole,
      name: 'LV Pole',
    },
    {
      key: CodedAssetType.Building,
      name: 'Building',
    },
    {
      key: CodedAssetType.Customer,
      name: 'Customer',
    },
  ];

  showPrefix: boolean = false;

  constructor(
    private utilityService: UtilityService,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private router: Router,
    private appRef: ApplicationRef,
    private substationTemplateService: SubstationTemplateService,
    private blobStorageService: BlobStorageService
  ) { }

  ngOnInit(): void {
    this.blobStorageService.setContainer(ContainerNames.KYC);
    const routeId = this.route.snapshot.paramMap.get('id');
    this.id = parseInt(routeId == null ? '0' : routeId);
    this.get(this.id);
    this.bpsAdminLevel = parseInt(localStorage.getItem('bpsAdminLevel') || '0');
  }
  selectValidationRule(rule: ValidationRule, index: number) {
    const normalizedPropertyType = this.normalizeValidationRulePropertyType(rule.propertyType);
    const normalizedScopeId = this.normalizeScopeId((rule as ScopedValidationRule).scopeId);
    const specialConfig = this.getSpecialConfig(normalizedPropertyType);

    this.isEditingValidationRule = true;
    this.originalValidationRuleIndex = index;
    this.originalValidationRulePropertyType = normalizedPropertyType;
    this.originalValidationRuleScopeId = normalizedScopeId;
    this.selectedValidationRule = {
      ...rule,
      propertyType: normalizedPropertyType,
      scopeId: normalizedScopeId,
      scopeType: specialConfig?.scopeType ?? null,
      isAlfaNumeric: typeof rule.isAlfaNumeric === 'boolean' ? rule.isAlfaNumeric : null,
    };
    this.ensureOptionsLoaded(normalizedPropertyType);
    this.updateAvailableValidationRuleTypes(true);
  }

  selectConfigurationGeneration(config: ConfigurationGeneration) {
    this.selectedConfigurationGeneration = config;
    this.updateAvailableConfigurationGenerationTypes(true);
  }

  openNewValidationRule() {
    this.isEditingValidationRule = false;
    this.originalValidationRuleIndex = null;
    this.originalValidationRulePropertyType = null;
    this.originalValidationRuleScopeId = null;
    this.selectedValidationRule = this.createEmptyValidationRule();
    this.selectedValidationRule.isAlfaNumeric = null;
    this.updateAvailableValidationRuleTypes(false);
  }

  openNewConfigurationGeneration() {
    this.isEditConfigurationGeneration = false;
    this.selectedConfigurationGeneration = new ConfigurationGeneration();
    this.updateAvailableConfigurationGenerationTypes(false);
  }

  saveValidationRule() {
    if (!this.selectedUtility.validationRules) {
      this.selectedUtility.validationRules = [];
    }

    const normalizedPropertyType = this.normalizeValidationRulePropertyType(
      this.selectedValidationRule.propertyType
    );
    this.selectedValidationRule.propertyType = normalizedPropertyType;
    if (typeof normalizedPropertyType !== 'number') {
      this.toastr.warning('Please select property type.');
      return;
    }

    const specialConfig = this.getSpecialConfig(normalizedPropertyType);
    let selectedScopeId: number | null = null;

    if (specialConfig) {
      selectedScopeId = this.normalizeScopeId(this.selectedValidationRule.scopeId);
      if (selectedScopeId == null) {
        this.toastr.warning(`${specialConfig.fieldLabel} is required.`);
        return;
      }

      const selectedScopeOption = this.activeScopeOptions.find((option) => option.id === selectedScopeId);
      this.selectedValidationRule.scopeType = specialConfig.scopeType;
      this.selectedValidationRule.scopeId = selectedScopeId;
      this.selectedValidationRule.scopeLabel = selectedScopeOption?.label ?? null;
    } else {
      this.clearScopeFields(this.selectedValidationRule);
    }

    const isSameRuleKey = (item: ValidationRule, propertyType: number, scopeId: number | null): boolean => {
      if (item.propertyType !== propertyType) {
        return false;
      }

      const itemSpecialConfig = this.getSpecialConfig(propertyType);
      if (!itemSpecialConfig) {
        return true;
      }

      const itemScopeId = this.normalizeScopeId((item as ScopedValidationRule).scopeId);
      return itemScopeId === scopeId;
    };

    let index = -1;
    if (
      this.isEditingValidationRule &&
      this.originalValidationRuleIndex != null &&
      this.originalValidationRuleIndex >= 0 &&
      this.originalValidationRuleIndex < this.selectedUtility.validationRules.length
    ) {
      index = this.originalValidationRuleIndex;
    } else if (this.isEditingValidationRule && this.originalValidationRulePropertyType != null) {
      index = this.selectedUtility.validationRules.findIndex((item) =>
        isSameRuleKey(item, this.originalValidationRulePropertyType!, this.originalValidationRuleScopeId)
      );
    }

    if (index < 0) {
      index = this.selectedUtility.validationRules.findIndex((item) =>
        isSameRuleKey(item, normalizedPropertyType, selectedScopeId)
      );
    }

    const duplicateIndex = this.selectedUtility.validationRules.findIndex(
      (item, itemIndex) =>
        itemIndex !== index && isSameRuleKey(item, normalizedPropertyType, selectedScopeId)
    );

    if (duplicateIndex >= 0) {
      this.toastr.warning('Validation rule with the same property type and scope already exists.');
      return;
    }

    if (index >= 0) {
      this.selectedUtility.validationRules[index] = { ...this.selectedValidationRule };
    } else {
      this.selectedUtility.validationRules.push({ ...this.selectedValidationRule });
    }
    this.closeValidationModal?.nativeElement?.click();
  }

  validateConfigurationGeneration(): boolean {
    const normalizedPropertyType = this.normalizeConfigurationGenerationPropertyType(
      this.selectedConfigurationGeneration.propertyType
    );
    this.selectedConfigurationGeneration.propertyType = normalizedPropertyType;
    if (typeof normalizedPropertyType !== 'number') {
      this.toastr.error('Please select property type.');
      return false;
    }

    if (!this.selectedConfigurationGeneration.formatType) {
      this.toastr.error('Please select format type.');
      return false;
    }

    if (!this.selectedConfigurationGeneration.length || this.selectedConfigurationGeneration.length <= 0) {
      this.toastr.error('Please enter a valid character length.');
      return false;
    }

    if (this.selectedConfigurationGeneration.startsWith.length >= this.selectedConfigurationGeneration.length) {
      this.toastr.error('The "Starts With" length must be less than the total length.');
      return false;
    }

    // 1. Slucaj -> Numeric
    if (this.selectedConfigurationGeneration.formatType === +FormatType.Numeric) {
      // Treba osigurati da startsWith sadrzi samo brojeve
      if (!this.isOnlyNumbers(this.selectedConfigurationGeneration.startsWith)) {
        this.toastr.error('For Numeric format type, "Starts With" can contain only numbers.');
        return false;
      }
      // Treba osigurati da je moguce izvalidirati bar milion kombinacija
    }

    return true;
  }

  private isOnlyNumbers(str: string): boolean {
    return /^[0-9]*$/.test(str);
  }


  saveConfigurationGeneration() {
    if (!this.validateConfigurationGeneration()) return;

    if (this.selectedUtility.configurationGenerations == undefined)
      this.selectedUtility.configurationGenerations = [];
    const index = this.selectedUtility.configurationGenerations.findIndex(
      (element) => element.propertyType === this.selectedConfigurationGeneration.propertyType
    );
    if (index >= 0) {
      this.selectedUtility.configurationGenerations[index] = { ...this.selectedConfigurationGeneration };
    } else {
      this.selectedUtility.configurationGenerations.push({ ...this.selectedConfigurationGeneration });
    }
    this.closeConfigurationGenerationModal?.nativeElement?.click();
  }

  resetValidationRuleModal() {
    this.isEditingValidationRule = false;
    this.originalValidationRuleIndex = null;
    this.originalValidationRulePropertyType = null;
    this.originalValidationRuleScopeId = null;
    this.selectedValidationRule = this.createEmptyValidationRule();
    this.selectedValidationRule.isAlfaNumeric = null;
    this.updateAvailableValidationRuleTypes(false);
  }

  updateAvailableValidationRuleTypes(includeSelected: boolean) {
    const usedTypes = new Set(
      (this.selectedUtility?.validationRules || [])
        .map((rule) => rule.propertyType)
        .filter((type): type is number => typeof type === 'number')
    );

    if (includeSelected && typeof this.selectedValidationRule.propertyType === 'number') {
      usedTypes.delete(this.selectedValidationRule.propertyType);
    }

    this.availableValidationRuleTypeOptions = this.validationRuleTypeOptions.filter(
      (type) => this.isSpecialType(type.value) || !usedTypes.has(type.value)
    );
  }

  updateAvailableConfigurationGenerationTypes(includeSelected: boolean) {
    const usedTypes = new Set(
      (this.selectedUtility?.configurationGenerations || [])
        .map((c) => c.propertyType)
        .filter((type): type is number => typeof type === 'number')
    );

    if (includeSelected && typeof this.selectedConfigurationGeneration.propertyType === 'number') {
      usedTypes.delete(this.selectedConfigurationGeneration.propertyType);
    }

    this.availableConfigurationGenerationTypeOptions = this.configurationGenerationTypeOptions.filter(
      (type) => !usedTypes.has(type.value)
    );
  }

  onValidationRuleTypeChange(value: number | string | null) {
    const normalizedPropertyType = this.normalizeValidationRulePropertyType(value);
    this.selectedValidationRule.propertyType = normalizedPropertyType;

    const config = this.getSpecialConfig(normalizedPropertyType);
    if (!config) {
      this.clearScopeFields(this.selectedValidationRule);
      return;
    }

    this.selectedValidationRule.scopeType = config.scopeType;
    this.ensureOptionsLoaded(normalizedPropertyType);
  }

  onConfigurationGenerationTypeChange(value: number | string | null) {
    this.selectedConfigurationGeneration.propertyType = this.normalizeConfigurationGenerationPropertyType(value);
  }

  getValidationRuleLabel(propertyType: number | null) {
    if (propertyType == null) {
      return '';
    }
    return this.validationRuleTypeOptions.find((type) => type.value === propertyType)?.label ?? '';
  }

  getValidationRuleScopeLabel(rule: ValidationRule): string {
    if (!this.isSpecialType(rule.propertyType)) {
      return '-';
    }

    const scopedRule = rule as ScopedValidationRule;
    if (scopedRule.scopeLabel) {
      return scopedRule.scopeLabel;
    }

    const scopeId = this.normalizeScopeId(scopedRule.scopeId);
    if (scopeId == null) {
      return 'NA';
    }

    return this.getScopeOptionsForPropertyType(rule.propertyType).find((option) => option.id === scopeId)?.label ?? 'NA';
  }

  private normalizeValidationRulePropertyType(propertyType: number | string | null | { value?: unknown }) {
    if (typeof propertyType === 'number') {
      return propertyType;
    }

    if (!propertyType) {
      return null;
    }

    if (typeof propertyType === 'object' && typeof propertyType.value === 'number') {
      return propertyType.value;
    }

    const numericValue = Number(propertyType);
    if (!Number.isNaN(numericValue) && numericValue > 0) {
      return numericValue;
    }

    const normalized = propertyType.toString().trim().toLowerCase();
    const option = this.validationRuleTypeOptions.find(
      (type) => type.label.toLowerCase() === normalized
    );

    return option?.value ?? null;
  }

  private loadOptionsForLoaderKey(loaderKey: string) {
    if (this.optionsByLoaderKey[loaderKey] !== undefined) {
      return;
    }

    if (loaderKey === 'substationTemplate') {
      if (!this.id || this.id < 0) {
        this.optionsByLoaderKey[loaderKey] = [];
        return;
      }

      this.substationTemplateService.getAllForSelectByUtility(this.id).subscribe({
        next: (response: any) => {
          const responseData = response?.body;
          const templates = responseData?.data ?? [];
          this.optionsByLoaderKey[loaderKey] = templates
            .filter((template: any) => typeof template?.id === 'number')
            .map((template: any) => ({
              id: template.id,
              label: template.className,
            }));
          this.hydrateScopeLabelsForLoaderKey(loaderKey);
          this.appRef.tick();
        },
        error: (_) => {
          this.optionsByLoaderKey[loaderKey] = [];
          this.toastr.error('Error occurred while loading substation templates.');
        },
      });
      return;
    }

    this.optionsByLoaderKey[loaderKey] = [];
  }

  private getSpecialConfig(propertyType: number | null | undefined): SpecialValidationTypeConfig | null {
    if (propertyType == null) {
      return null;
    }

    return this.specialTypeMap[propertyType] ?? null;
  }

  private isSpecialType(propertyType: number | null | undefined): boolean {
    return this.getSpecialConfig(propertyType) != null;
  }

  private ensureOptionsLoaded(propertyType: number | null | undefined) {
    const config = this.getSpecialConfig(propertyType);
    if (!config) {
      return;
    }

    this.loadOptionsForLoaderKey(config.loaderKey);
  }

  private preloadScopeOptionsForExistingRules() {
    if (!this.selectedUtility.validationRules?.length) {
      return;
    }

    const propertyTypesToLoad = Array.from(
      new Set(
        this.selectedUtility.validationRules
          .map((rule) => this.normalizeValidationRulePropertyType(rule.propertyType))
          .filter((propertyType): propertyType is number => this.isSpecialType(propertyType))
      )
    );

    propertyTypesToLoad.forEach((propertyType) => this.ensureOptionsLoaded(propertyType));
  }

  private hydrateScopeLabelsForLoaderKey(loaderKey: string) {
    const options = this.optionsByLoaderKey[loaderKey] ?? [];
    if (!options.length || !this.selectedUtility.validationRules?.length) {
      return;
    }

    this.selectedUtility.validationRules = this.selectedUtility.validationRules.map((rule) => {
      const propertyType = this.normalizeValidationRulePropertyType(rule.propertyType);
      const config = this.getSpecialConfig(propertyType);

      if (!config || config.loaderKey !== loaderKey) {
        return rule;
      }

      const scopeId = this.normalizeScopeId((rule as ScopedValidationRule).scopeId);
      const scopeLabel = options.find((option) => option.id === scopeId)?.label ?? null;

      return {
        ...rule,
        scopeLabel,
      } as ScopedValidationRule;
    });
  }

  private clearScopeFields(rule: ScopedValidationRule) {
    rule.scopeType = null;
    rule.scopeId = null;
    rule.scopeLabel = null;
  }

  private normalizeScopeId(value: unknown): number | null {
    if (typeof value === 'number' && value >= 0) {
      return value;
    }

    if (typeof value === 'string') {
      const numericValue = Number(value);
      if (!Number.isNaN(numericValue) && numericValue >= 0) {
        return numericValue;
      }
    }

    return null;
  }

  private createEmptyValidationRule(): ScopedValidationRule {
    return new ValidationRule();
  }

  getScopeOptionsForPropertyType(propertyType: number | null | undefined): ScopeOption[] {
    const config = this.getSpecialConfig(propertyType);
    if (!config) {
      return [];
    }

    return this.optionsByLoaderKey[config.loaderKey] ?? [];
  }

  get activeSpecialConfig(): SpecialValidationTypeConfig | null {
    return this.getSpecialConfig(this.selectedValidationRule.propertyType);
  }

  get activeScopeOptions(): ScopeOption[] {
    return this.getScopeOptionsForPropertyType(this.selectedValidationRule.propertyType);
  }

  private normalizeConfigurationGenerationPropertyType(propertyType: number | string | null) {
    if (typeof propertyType === 'number') {
      return propertyType;
    }

    if (!propertyType) {
      return null;
    }

    const numericValue = Number(propertyType);
    if (!Number.isNaN(numericValue) && numericValue > 0) {
      return numericValue;
    }

    const normalized = propertyType.toString().trim().toLowerCase();
    const option = this.configurationGenerationTypeOptions.find(
      (type) => type.label.toLowerCase() === normalized
    );

    return option?.value ?? null;
  }

  selectCurrency(e: any) {
    if (e.target.value != '') {
      this.selectedUtility.currency = e.target.value;
    } else {
      this.selectedUtility.currency = '';
    }
  }

  selectPattern(e: any) {
    if (e.target.value != '') {
      this.selectedCodePattern.codePatternOption =
        CodePatternOptions[e.target.value as keyof typeof CodePatternOptions];
      this.checkPrefixVisibility();
    }
  }

  selectCountry(e: any) {
    if (e.target.value != '') {
      this.selectedUtility.country = e.target.value;
    } else {
      this.selectedUtility.country = '';
    }
  }

  selectLanguage(e: any) {
    if (e.target.val != '') {
      this.selectedUtility.language = e.target.value;
    } else {
      this.selectedUtility.language = '';
    }
  }

  get(id: number) {
    this.utilityService.get(id).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.selectedUtility = responseData.data;
          this.selectedUtility.defaultCaimsCodePatterns = this.selectedUtility.defaultCaimsCodePatterns ?? true;
          this.selectedUtility.isSelectedAreasForMobileLimited = !this.selectedUtility.isSelectedAreasForMobileLimited ? false : this.selectedUtility.isSelectedAreasForMobileLimited;
          this.selectedUtility.configurationGenerations =
            (responseData.data.configurationGeneration || []).map((item: any) =>
              ConfigurationGeneration.fromData(item)
            );
          if (this.selectedUtility.configurationGenerations.length >= 2) {
            this.isAddNewConfigurationGenerationEnabled = false;
          }
          this.selectedUtility.fullName =
            this.selectedUtility.fullName ?? this.selectedUtility.name;
          this.selectedUtility.kycConsentConfiguration =
            this.selectedUtility.kycConsentConfiguration ??
            new KycConsentConfig();
          this.selectedUtility.bankDetails =
            this.selectedUtility.bankDetails ?? new BankDetails();
          this.imageSrc = this.selectedUtility.imageUrl;
          this.selectedUtility.codePatterns =
            this.selectedUtility.codePatterns.map((item) => ({
              ...item,
              displayPatternOption: this.presentCodePatternOption(
                item.codePatternOption
              ),
              displayAssetType: this.presentAssetTypes(item.assetType),
            }));
          this.selectedUtility.validationRules = (this.selectedUtility.validationRules || []).map((rule) => ({
            ...rule,
            propertyType: this.normalizeValidationRulePropertyType(rule.propertyType),
            scopeId: this.normalizeScopeId((rule as ScopedValidationRule).scopeId),
            isAlfaNumeric: typeof rule.isAlfaNumeric === 'boolean' ? rule.isAlfaNumeric : null,
          }));
          this.preloadScopeOptionsForExistingRules();

          // if (!this.selectedUtility.mapConfiguration) {
          //   this.selectedUtility.mapConfiguration = new MapConfiguration();
          //   this.selectedUtility.mapConfiguration.latitude = 9.1021111; // default Africa coords
          //   this.selectedUtility.mapConfiguration.longitude = 18.2811944;
          //   this.selectedUtility.mapConfiguration.zoom = 11;
          // }
          this.resolveEnum();
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }
  isChecked(listConfig: any) {
    let checked = true;
    listConfig.forEach((val: any) => {
      if (!this.selectedUtility.dashboardConfiguration.includes(val))
        checked = false;
    });
    return checked;
  }
  onChange(configuration: any, event: any = null) {
    configuration.forEach((value: any) => {
      if (event != null) {
        if (!this.isChecked([value]) && event.target.checked == true)
          this.selectedUtility.dashboardConfiguration.push(value);
        else if (this.isChecked([value]) && event.target.checked == false)
          this.selectedUtility.dashboardConfiguration =
            this.selectedUtility.dashboardConfiguration.filter(
              (item: any) => item != value
            );
      } else {
        if (this.selectedUtility.dashboardConfiguration.includes(value))
          this.selectedUtility.dashboardConfiguration =
            this.selectedUtility.dashboardConfiguration.filter(
              (item: any) => item != value
            );
        else this.selectedUtility.dashboardConfiguration.push(value);
      }
    });

    // Special rule: if Meter Validation is turned off, also uncheck Tamper
    if (
      configuration.includes(this.Configuration.MeterValidation) &&
      !this.isChecked([this.Configuration.MeterValidation])
    ) {
      this.selectedUtility.dashboardConfiguration =
        this.selectedUtility.dashboardConfiguration.filter(
          (item: any) => item !== this.Configuration.MeterValidationTamper
        );
    }
  }
  edit() {
    this.editEnabled = true;
  }

  validate() {
    if (this.selectedUtility.subdomain === '') {
      this.toastr.warning('Please fill out the subdomain field.');
      return;
    }
    if (this.selectedUtility.colorCode === '') {
      this.toastr.warning('Please select color code.');
      return;
    }
    if (this.selectedUtility.name === '') {
      this.toastr.warning('Please fill out the name field.');
      return;
    }
    if (this.selectedUtility.imageUrl == '' && this.utilityImage == null) {
      this.toastr.warning('Please add the utility logo.');
      return;
    }
    if (this.selectedUtility.dashboardConfiguration.length === 0) {
      this.toastr.warning('Please select some Assets/Operations.');
      return;
    }
    if (
      this.selectedUtility.country == '' ||
      this.selectedUtility.country == null ||
      this.selectedUtility.country == undefined
    ) {
      this.toastr.warning('Please select country.');
      return;
    }
    if (
      this.selectedUtility.language == '' ||
      this.selectedUtility.language == null ||
      this.selectedUtility.language == undefined
    ) {
      this.toastr.warning('Please select language.');
      return;
    }
    if (
      !(!!this.selectedUtility.sendgridApiKey === !!this.selectedUtility.senderEmail)
    ) {
      this.toastr.warning('Please fill out both the Email Sender Address and API Key fields or leave them both blank.');
      return;
    }
    this.save();
  }
  save() {
    this.utilityService.add(this.selectedUtility, this.utilityImage).subscribe({
      next: (response) => {
        const responseData = response;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          if (responseData.message === '') {
            this.toastr.success('Successfully added utility');
            this.router.navigate(['/utility-management']);
          } else {
            this.toastr.warning(responseData.message);
          }
        } else {
          this.toastr.error(responseData?.data);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  removeImage() {
    this.utilityImage = null;
    this.imageSrc = '';
  }
  onFileDropped($event: any) {
    this.utilityImage = $event.item(0);
    if (
      this.utilityImage?.type != 'image/png' &&
      this.utilityImage?.type != 'image/jpg' &&
      this.utilityImage?.type != 'image/jpeg'
    ) {
      this.toastr.warning('Only PNG/JPG/JPEG images are allowed');
      if (this.utilityImageInput?.nativeElement) {
        this.utilityImageInput.nativeElement.value = '';
      }
    } else {
      const reader = new FileReader();
      var width = 0;
      var height = 0;
      reader.onload = (e) => {
        this.imageSrc = reader.result!;
        const img = new Image();
        img.src = e.target!.result as string;

        img.onload = () => {
          width = img.width;
          height = img.height;
          var ratio = width / height;
          if (ratio > 2 || ratio < 0.5) {
            this.toastr.warning('Unsuported image size.');
            this.imageSrc = '';
            this.utilityImage = null;
            this.appRef.tick();
          }
        };
      };
      reader.readAsDataURL(this.utilityImage!);
    }
  }
  fileBrowserHandler($event: any) {
    this.utilityImage = $event.target.files.item(0);
    if (
      this.utilityImage?.type != 'image/png' &&
      this.utilityImage?.type != 'image/jpg' &&
      this.utilityImage?.type != 'image/jpeg'
    ) {
      this.toastr.warning('Only PNG/JPG/JPEG images are allowed');
      if (this.utilityImageInput?.nativeElement) {
        this.utilityImageInput.nativeElement.value = '';
      }
    } else {
      const reader = new FileReader();
      var width = 0;
      var height = 0;
      reader.onload = (e) => {
        this.imageSrc = reader.result!;
        const img = new Image();
        img.src = e.target!.result as string;
        img.onload = () => {
          width = img.width;
          height = img.height;
          var ratio = width / height;
          if (ratio > 2 || ratio < 0.5) {
            this.toastr.warning('Unsuported image size.');
            this.imageSrc = '';
            this.utilityImage = null;
            this.appRef.tick();
          }
        };
      };

      reader.readAsDataURL(this.utilityImage!);
    }
  }
  resolveEnum() {
    var enums: any[] = [];
    var enumm: DashboardConfiguration;
    this.selectedUtility.dashboardConfiguration.forEach((obj) => {
      enumm = obj as DashboardConfiguration;
      enums.push(DashboardConfiguration[enumm]);
    });
    this.selectedUtility.dashboardConfiguration = enums;
  }

  saveTranslation() {
    var indexToChange = this.selectedUtility.translations.findIndex(
      (item) => item.key == this.selectedTranslation.key
    );
    this.selectedUtility.translations[indexToChange] = this.selectedTranslation;
    this.closeModal?.nativeElement?.click();
  }

  onCodePatternsToggle(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.selectedUtility.defaultCaimsCodePatterns = isChecked;
  }

  onCinPatternsToggle(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.selectedUtility.defaultCaimsCodePatterns = !isChecked;
  }

  saveCodePattern() {
    this.selectedCodePattern.displayPatternOption = this.presentCodePatternOption(this.selectedCodePattern.codePatternOption);
    var indexToChange = this.selectedUtility.codePatterns.findIndex(
      (item) => item.assetType == this.selectedCodePattern.assetType
    );
    this.selectedUtility.codePatterns[indexToChange] = this.selectedCodePattern;
    this.selectedCodePattern = new CodePattern();
    this.closeCodeModal?.nativeElement?.click();
  }

  selectTranslation(item: Translation) {
    this.selectedTranslation = { ...item };
  }

  selectCodePattern(item: CodePattern) {
    this.selectedCodePattern = { ...item };
    this.selectedCodePatternOption = this.selectedCodePattern.codePatternOption ?
      CodePatternOptions[this.selectedCodePattern.codePatternOption?.toString() as keyof typeof CodePatternOptions] : undefined;
    this.checkPrefixVisibility(item.codePatternOption);
  }

  checkPrefixVisibility(item?: CodePatternOptions) {
    const pattern =
      CodePatternOptions[item ? item : this.selectedCodePattern.codePatternOption?.toString() as keyof typeof CodePatternOptions];
    if (
      pattern == CodePatternOptions.AlphaNumericSingle ||
      pattern == CodePatternOptions.AlphaNumericDouble ||
      pattern == CodePatternOptions.AlphaNumericTriple ||
      pattern == CodePatternOptions.AlphaNumericQuadruple
    ) {
      this.showPrefix = true;
    } else {
      this.showPrefix = false;
    }
  }

  presentCodePatternOption(option?: CodePatternOptions) {
    if (option) {
      const pattern =
        CodePatternOptions[
        option?.toString() as keyof typeof CodePatternOptions
        ];
      var res = this.codePatternOptions.find((x) => x.key == pattern)?.value;
      return res;
    }
    return '';
  }

  presentAssetTypes(type?: CodedAssetType) {
    if (type) {
      const assetType =
        CodedAssetType[type?.toString() as keyof typeof CodedAssetType];
      var res = this.assetTypeOptions.find((x) => x.key == assetType)?.name;
      return res;
    }
    return '';
  }

  clearCodePattern() {
    this.selectedCodePatternOption = undefined;
    this.selectedCodePattern = new CodePattern();
  }

  onMapConfigurationSaved(config: MapConfiguration) {
    this.selectedUtility.mapConfiguration = config;
    if (this.closeMapModalBtn) {
      this.closeMapModalBtn.nativeElement.click();
    }
  }

  onRowClick(item: any): void {
    if (item.id === 0) {
      this.selectConfigurationGeneration(item);
    }
  }

  delete(event: MouseEvent, config: ConfigurationGeneration) {
    event.stopPropagation();
    event.preventDefault();

    this.selectedUtility.configurationGenerations = this.selectedUtility.configurationGenerations?.filter(x => x.propertyType !== config.propertyType);
    if (this.selectedUtility.configurationGenerations?.length < 2) {
      this.isAddNewConfigurationGenerationEnabled = false;
    }
  }
  onServiceFileDropped($event: any) {
    this.serviceImage = $event.item(0);
    if (
      this.serviceImage?.type != 'image/png' &&
      this.serviceImage?.type != 'image/jpg' &&
      this.serviceImage?.type != 'image/jpeg'
    ) {
      this.toastr.warning('Only PNG/JPG/JPEG images are allowed');
      if (this.serviceImageInput?.nativeElement) {
        this.serviceImageInput.nativeElement.value = '';
      }
    } else {
      const reader = new FileReader();
      const maxSize = 2 * 1024 * 1024;
      if (this.serviceImage && this.serviceImage.size > maxSize) {
        this.toastr.warning('Image must be smaller than 2 MB.');
        this.imageSrc = '';
        this.serviceImage = null;
        this.appRef.tick();
        return;
      }

      this.serviceImageUpload(this.serviceImage);
    }
  }
  serviceFileBrowserHandler($event: any) {
    this.serviceImage = $event.target.files.item(0);
    if (
      this.serviceImage?.type != 'image/png' &&
      this.serviceImage?.type != 'image/jpg' &&
      this.serviceImage?.type != 'image/jpeg'
    ) {
      this.toastr.warning('Only PNG/JPG/JPEG images are allowed');
      if (this.serviceImageInput?.nativeElement) {
        this.serviceImageInput.nativeElement.value = '';
      }
    } else {

      const maxSize = 2 * 1024 * 1024;
      if (this.serviceImage && this.serviceImage.size > maxSize) {
        this.toastr.warning('Image must be smaller than 2 MB.');
        this.imageSrc = '';
        this.serviceImage = null;
        this.appRef.tick();
        return;
      }
      this.serviceImageUpload(this.serviceImage);

    }
  }
  removeServiceImage() {
    this.serviceImage = null;
    this.selectedUtility.kycConsentConfiguration.serviceRequestImageUrl = null;
  }
  serviceImageUpload(data: File | null) {
    if (data === null) return;

    this.blobStorageService.uploadFile(
      data,
      data.name,
      (response) => {
        this.selectedUtility.kycConsentConfiguration.serviceRequestImageUrl = response;
      },
      (error) => {

        this.toastr.error('Failed to upload customer signature');
      }
    );
  }

  onLimitChange(value: number) {
    if (value < 1)
      this.limitValue = 1;
  }

  validateLimit() {
    if (!this.limitValue || this.limitValue < 1)
      this.limitValue = 1;
  }
}
